<template>
  <span :class="classes">
    <slot>{{ label }}</slot>
    <button
      v-if="removable"
      type="button"
      :aria-label="ariaLabelRemove"
      class="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-black/5 focus-visible:outline-none focus-visible:shadow-[var(--shadow-ring-focus)]"
      @click="$emit('remove')"
    >
      <BaseIcon name="close" :size="16" />
    </button>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import BaseIcon from '@/components/base/BaseIcon.vue';

const tagVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-3 py-0.5 text-xs font-semibold',
  {
    variants: {
      tone: {
        default: 'bg-paper-muted text-ink',
        accent: 'bg-accent-soft text-accent-strong',
        success: 'bg-success-50 text-success-700',
        warning: 'bg-warning-100 text-warning-700',
        danger: 'bg-danger-100 text-danger-700',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  },
);

export type TagTone = NonNullable<VariantProps<typeof tagVariants>['tone']>;

interface Props {
  tone?: TagTone;
  removable?: boolean;
  label?: string;
  ariaLabelRemove?: string;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'default',
  removable: false,
  label: '',
  ariaLabelRemove: 'Remove',
});

defineEmits<{
  (e: 'remove'): void;
}>();

const classes = computed(() => tagVariants({ tone: props.tone }));
</script>
